import React, { useState } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Input,
  Select,
  HStack,
  Spinner,
  Center,
  Text,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { 
  useAdminProperties, 
  Property, 
  useVerifyLocation, 
  useUnverifyLocation, 
  useDisableLocation, 
  useEnableLocation, 
  usePromoteLocation, 
  useUnpromoteLocation 
} from "api/admin";

export default function Properties(): JSX.Element {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isVerified, setIsVerified] = useState("");
  const [isDisabled, setIsDisabled] = useState("");
  
  const { data, isLoading, isError } = useAdminProperties({ page, limit: 10, search, isVerified, isDisabled });
  const verifyMut = useVerifyLocation();
  const unverifyMut = useUnverifyLocation();
  const disableMut = useDisableLocation();
  const enableMut = useEnableLocation();
  const promoteMut = usePromoteLocation();
  const unpromoteMut = useUnpromoteLocation();
  const toast = useToast();

  const handleAction = async (action: any, id: string, name: string) => {
    try {
      await action.mutateAsync(id);
      toast({ title: `Successfully updated ${name}`, status: "success", isClosable: true });
    } catch (e) {
      toast({ title: "Action failed", status: "error", isClosable: true });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="20px" mb="20px">
        <HStack mb="20px" spacing={4}>
          <Input placeholder="Search properties" value={search} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setSearch(e.target.value)} />
          <Select placeholder="Verification Status" value={isVerified} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setIsVerified(e.target.value)}>
            <option value="true">Verified</option>
            <option value="false">Unverified</option>
          </Select>
          <Select placeholder="Enable Status" value={isDisabled} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setIsDisabled(e.target.value)}>
            <option value="false">Active</option>
            <option value="true">Disabled</option>
          </Select>
        </HStack>

        {isLoading ? (
          <Center p="40px"><Spinner /></Center>
        ) : isError ? (
          <Center p="40px"><Text color="red.500">Failed to load data.</Text></Center>
        ) : !data?.data?.length ? (
          <Center p="40px"><Text>No data found.</Text></Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Vendor</Th>
                  <Th>Price</Th>
                  <Th>Badges</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.data?.map((p: Property) => (
                  <Tr key={p.id}>
                    <Td>{p.name}</Td>
                    <Td>{p.vendor?.fullName}</Td>
                    <Td>₦{(p.price ?? 0).toLocaleString()}</Td>
                    <Td>
                      <HStack spacing={1}>
                        <Badge colorScheme={p.isVerified ? "green" : "yellow"}>
                          {p.isVerified ? "Verified" : "Pending"}
                        </Badge>
                        {p.isDisabled && <Badge colorScheme="red">Disabled</Badge>}
                        {p.isPromoted && <Badge colorScheme="purple">Promoted</Badge>}
                      </HStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {p.isVerified ? (
                          <Button size="sm" onClick={() => handleAction(unverifyMut, p.id, p.name)}>Unverify</Button>
                        ) : (
                          <Button size="sm" colorScheme="green" onClick={() => handleAction(verifyMut, p.id, p.name)}>Verify</Button>
                        )}
                        {p.isDisabled ? (
                          <Button size="sm" colorScheme="blue" onClick={() => handleAction(enableMut, p.id, p.name)}>Enable</Button>
                        ) : (
                          <Button size="sm" colorScheme="red" onClick={() => handleAction(disableMut, p.id, p.name)}>Disable</Button>
                        )}
                        {p.isPromoted ? (
                          <Button size="sm" onClick={() => handleAction(unpromoteMut, p.id, p.name)}>Unpromote</Button>
                        ) : (
                          <Button size="sm" colorScheme="purple" onClick={() => handleAction(promoteMut, p.id, p.name)}>Promote</Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Flex justify="space-between" mt="4">
              <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Text>Page {data?.page} of {(data?.totalPage || 0)}</Text>
              <Button disabled={page >= (data?.totalPage || 0)} onClick={() => setPage(p => p + 1)}>Next</Button>
            </Flex>
          </Box>
        )}
      </Card>
    </Box>
  );
}
